import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DEFAULT_VIEW_MODES, type ViewMode } from "./view-mode";

export interface UseListViewModeOptions {
  storageKey: string;
  defaultMode?: ViewMode;
  allowedModes?: ViewMode[];
  paramName?: string;
}

export interface UseListViewModeResult {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  allowedModes: ViewMode[];
}

function isAllowed(
  mode: string | null | undefined,
  allowed: ViewMode[],
): mode is ViewMode {
  return Boolean(mode && allowed.includes(mode as ViewMode));
}

/**
 * Persists list view mode via `?view=` URL param and localStorage.
 */
export function useListViewMode({
  storageKey,
  defaultMode = "cards",
  allowedModes = DEFAULT_VIEW_MODES,
  paramName = "view",
}: UseListViewModeOptions): UseListViewModeResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const fallback = allowedModes.includes(defaultMode)
    ? defaultMode
    : (allowedModes[0] ?? "cards");

  const [viewMode, setViewModeState] = useState<ViewMode>(() => {
    if (typeof window === "undefined") return fallback;
    const urlMode = new URLSearchParams(window.location.search).get(paramName);
    if (isAllowed(urlMode, allowedModes)) return urlMode;
    const stored = localStorage.getItem(storageKey);
    if (isAllowed(stored, allowedModes)) return stored;
    return fallback;
  });

  useEffect(() => {
    const urlMode = searchParams.get(paramName);
    if (isAllowed(urlMode, allowedModes)) {
      setViewModeState(urlMode);
      localStorage.setItem(storageKey, urlMode);
    }
  }, [searchParams, paramName, allowedModes, storageKey]);

  const setViewMode = useCallback(
    (mode: ViewMode) => {
      if (!allowedModes.includes(mode)) return;
      setViewModeState(mode);
      localStorage.setItem(storageKey, mode);
      const next = new URLSearchParams(searchParams);
      next.set(paramName, mode);
      setSearchParams(next, { replace: true });
    },
    [allowedModes, storageKey, searchParams, setSearchParams, paramName],
  );

  return { viewMode, setViewMode, allowedModes };
}
